import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Activity, RefreshCw, Calendar, Filter,
  User, Heart, MessageSquare, Gift, LogIn, UserPlus,
  TrendingUp, Clock, Zap, ArrowRight, AlertTriangle,
  CheckCircle, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend, FunnelChart, Funnel,
  LabelList, Cell
} from "recharts";
import AdminLayout from "@/components/AdminLayout";
import { format } from "date-fns";

const EVENT_ICONS = {
  user_signup: UserPlus,
  user_login: LogIn,
  profile_update: User,
  match_created: Heart,
  message_sent: MessageSquare,
  match_accepted: CheckCircle,
  subscription_upgrade: TrendingUp
};

const EVENT_COLORS = {
  user_signup: "#22C55E",
  user_login: "#3B82F6",
  profile_update: "#8B5CF6",
  match_created: "#EC4899",
  message_sent: "#06B6D4",
  match_accepted: "#10B981",
  subscription_upgrade: "#1C3A7A"
};

const EVENT_LABELS = {
  user_signup: "Sign Ups",
  user_login: "Logins",
  profile_update: "Profile Updates",
  match_created: "Matches",
  message_sent: "Messages",
  match_accepted: "Matches Accepted",
  subscription_upgrade: "Upgrades"
};

const FUNNEL_COLORS = ["#22C55E", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B"];

const AdminEvents = () => {
  const navigate = useNavigate();
  const [eventsData, setEventsData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7);
  const [funnelDateRange, setFunnelDateRange] = useState(30);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [viewMode, setViewMode] = useState("summary");

  useEffect(() => {
    if (viewMode === "funnel") {
      fetchFunnelData();
    } else {
      fetchEventsData();
    }
  }, [dateRange, selectedEventType, viewMode, funnelDateRange]);

  const fetchEventsData = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminAnalyticsEvents(dateRange, selectedEventType);
      setEventsData(response.data);
    } catch (error) {
      toast.error("Failed to load events data");
    } finally {
      setLoading(false);
    }
  };

  const fetchFunnelData = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminAnalyticsFunnel(funnelDateRange);
      setFunnelData(response.data);
    } catch (error) {
      toast.error("Failed to load funnel data");
    } finally {
      setLoading(false);
    }
  };

  const dateRanges = [
    { label: "7 Days", value: 7 },
    { label: "14 Days", value: 14 },
    { label: "30 Days", value: 30 },
    { label: "60 Days", value: 60 }
  ];

  const formatEventType = (type) => EVENT_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  const getEventIcon = (type) => EVENT_ICONS[type] || Activity;
  
  const getEventColor = (type) => EVENT_COLORS[type] || "#6B7280";

  // Prepare chart data for stacked area chart
  const getStackedChartData = () => {
    if (!eventsData?.daily_totals) return [];
    
    const dataByDate = {};
    
    // Initialize dates from daily_totals
    eventsData.daily_totals.forEach(item => {
      dataByDate[item.date] = { date: item.date };
    });
    
    // Add event type counts
    Object.entries(eventsData.event_data || {}).forEach(([eventType, items]) => {
      items.forEach(item => {
        if (dataByDate[item.date]) {
          dataByDate[item.date][eventType] = item.count;
        }
      });
    });
    
    return Object.values(dataByDate).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Get summary cards data
  const getSummaryCards = () => {
    if (!eventsData?.summary) return [];
    
    return Object.entries(eventsData.summary)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([type, count]) => ({
        type,
        count,
        icon: getEventIcon(type),
        color: getEventColor(type),
        label: formatEventType(type)
      }));
  };

  return (
    <AdminLayout>
    <div className="bg-gray-50" data-testid="admin-events-page">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#1C3A7A]">Event Tracking</h1>
              <p className="text-sm text-gray-500">Custom analytics events</p>
            </div>
            <Button
              onClick={fetchEventsData}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Date Range Selector */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {dateRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => setDateRange(range.value)}
                data-testid={`date-range-${range.value}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  dateRange === range.value
                    ? 'bg-[#1C3A7A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          {/* Total Events Banner */}
          <div className="bg-gradient-to-r from-[#1C3A7A] to-indigo-600 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-200 text-sm mb-1">Total Events Tracked</p>
                <p className="text-4xl font-bold">{(eventsData?.total_events || 0).toLocaleString()}</p>
                <p className="text-indigo-200 text-sm mt-1">Last {dateRange} days</p>
              </div>
              <Activity className="w-16 h-16 text-indigo-300 opacity-50" />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {getSummaryCards().map((card, index) => (
              <button
                key={card.type}
                onClick={() => setSelectedEventType(selectedEventType === card.type ? null : card.type)}
                data-testid={`event-card-${card.type}`}
                className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all text-left ${
                  selectedEventType === card.type ? 'ring-2 ring-[#FAB710]' : ''
                }`}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-2"
                  style={{ backgroundColor: `${card.color}20` }}
                >
                  <card.icon className="w-5 h-5" style={{ color: card.color }} />
                </div>
                <p className="text-2xl font-bold text-[#1C3A7A]">{card.count.toLocaleString()}</p>
                <p className="text-xs text-gray-500 truncate">{card.label}</p>
              </button>
            ))}
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setViewMode("summary")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "summary" ? 'bg-[#1C3A7A] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setViewMode("funnel")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === "funnel" ? 'bg-[#1C3A7A] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Target className="w-4 h-4" />
              Conversion Funnel
            </button>
            <button
              onClick={() => setViewMode("timeline")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "timeline" ? 'bg-[#1C3A7A] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setViewMode("recent")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === "recent" ? 'bg-[#1C3A7A] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Recent Events
            </button>
          </div>

          {/* Conversion Funnel View */}
          {viewMode === "funnel" && (
            <div className="space-y-6">
              {/* Funnel Date Range */}
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">Period:</span>
                {[7, 14, 30, 60].map((days) => (
                  <button
                    key={days}
                    onClick={() => setFunnelDateRange(days)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      funnelDateRange === days
                        ? 'bg-[#FAB710] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>

              {/* Funnel Overview Banner */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-200 text-sm mb-1">Overall Conversion Rate</p>
                    <p className="text-4xl font-bold">{funnelData?.overall_conversion || 0}%</p>
                    <p className="text-emerald-200 text-sm mt-1">Sign Up → First Message</p>
                  </div>
                  <div className="text-right">
                    {funnelData?.biggest_drop_off && (
                      <div className="bg-white/20 rounded-lg px-4 py-2">
                        <p className="text-emerald-100 text-xs">Biggest Drop-off</p>
                        <p className="font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {funnelData.biggest_drop_off}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Funnel Stages */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#1C3A7A] mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#FAB710]" />
                  User Journey Funnel
                </h3>
                
                <div className="space-y-4">
                  {(funnelData?.funnel_stages || []).map((stage, index) => (
                    <div key={stage.stage} className="relative">
                      <div className="flex items-center gap-4">
                        {/* Stage Number */}
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                          style={{ backgroundColor: FUNNEL_COLORS[index] }}
                        >
                          {index + 1}
                        </div>
                        
                        {/* Stage Info */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">{stage.stage}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-bold text-[#1C3A7A]">
                                {stage.count.toLocaleString()}
                              </span>
                              <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                                stage.rate >= 50 ? 'bg-green-100 text-green-700' : 
                                stage.rate >= 25 ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-red-100 text-red-700'
                              }`}>
                                {stage.rate}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div 
                              className="h-3 rounded-full transition-all duration-500"
                              style={{ 
                                width: `${stage.rate}%`,
                                backgroundColor: FUNNEL_COLORS[index]
                              }}
                            />
                          </div>
                          
                          {/* Stage-to-Stage Rate */}
                          {index > 0 && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              <ArrowRight className="w-3 h-3" />
                              <span>{stage.stage_rate}% from previous stage</span>
                              {stage.drop_off > 0 && (
                                <span className="text-red-500">
                                  ({stage.drop_off.toLocaleString()} dropped)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Connector Line */}
                      {index < (funnelData?.funnel_stages?.length || 0) - 1 && (
                        <div className="absolute left-5 top-12 w-0.5 h-8 bg-gray-200" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h4 className="font-semibold text-[#1C3A7A] mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    What's Working
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {funnelData?.funnel_stages?.slice(1).filter(s => s.stage_rate >= 50).map(s => (
                      <li key={s.stage} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full" />
                        {s.stage}: {s.stage_rate}% conversion
                      </li>
                    ))}
                    {funnelData?.funnel_stages?.slice(1).filter(s => s.stage_rate >= 50).length === 0 && (
                      <li className="text-gray-400">No stages above 50% conversion yet</li>
                    )}
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h4 className="font-semibold text-[#1C3A7A] mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Needs Attention
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {funnelData?.funnel_stages?.slice(1).filter(s => s.stage_rate < 50 && s.drop_off > 0).map(s => (
                      <li key={s.stage} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-500 rounded-full" />
                        {s.stage}: {s.drop_off} users dropped ({100 - s.stage_rate}% loss)
                      </li>
                    ))}
                    {funnelData?.funnel_stages?.slice(1).filter(s => s.stage_rate < 50 && s.drop_off > 0).length === 0 && (
                      <li className="text-gray-400">All stages performing well!</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Chart - Summary View */}
          {viewMode === "summary" && (
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
              <h3 className="font-semibold text-[#1C3A7A] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#FAB710]" />
                Daily Event Activity
              </h3>
              
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={eventsData?.daily_totals || []}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1C3A7A" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1C3A7A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(val) => format(new Date(val), 'MMM d')}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(val) => format(new Date(val), 'MMMM d, yyyy')}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1C3A7A" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorTotal)"
                    name="Events"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Chart - Timeline View */}
          {viewMode === "timeline" && (
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
              <h3 className="font-semibold text-[#1C3A7A] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FAB710]" />
                Event Breakdown by Type
              </h3>
              
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={getStackedChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => format(new Date(val), 'MMM d')}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(val) => format(new Date(val), 'MMMM d, yyyy')}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Legend />
                  {Object.keys(eventsData?.event_data || {}).map((eventType) => (
                    <Bar 
                      key={eventType}
                      dataKey={eventType} 
                      stackId="a"
                      fill={getEventColor(eventType)}
                      name={formatEventType(eventType)}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Recent Events List */}
          {viewMode === "recent" && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-[#1C3A7A] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#FAB710]" />
                  Recent Events
                </h3>
              </div>
              
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {(eventsData?.recent_events || []).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No events recorded yet</p>
                    <p className="text-sm">Events will appear here as users interact with the app</p>
                  </div>
                ) : (
                  (eventsData?.recent_events || []).map((event, index) => {
                    const EventIcon = getEventIcon(event.event_type);
                    return (
                      <div 
                        key={event.id || index} 
                        className="p-4 hover:bg-gray-50 flex items-center gap-4"
                        data-testid={`event-row-${index}`}
                      >
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${getEventColor(event.event_type)}20` }}
                        >
                          <EventIcon 
                            className="w-5 h-5" 
                            style={{ color: getEventColor(event.event_type) }} 
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">
                            {formatEventType(event.event_type)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {event.user_name || 'Anonymous'} 
                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                              <span className="text-gray-400">
                                {' '}• {JSON.stringify(event.metadata).slice(0, 50)}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-gray-500">
                            {format(new Date(event.timestamp), 'MMM d')}
                          </p>
                          <p className="text-xs text-gray-400">
                            {format(new Date(event.timestamp), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Event Types Legend (when in summary mode) */}
          {viewMode === "summary" && Object.keys(eventsData?.summary || {}).length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-[#1C3A7A] mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#FAB710]" />
                All Event Types
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(eventsData?.summary || {}).map(([type, count]) => {
                  const Icon = getEventIcon(type);
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedEventType(selectedEventType === type ? null : type)}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                        selectedEventType === type 
                          ? 'bg-[#1C3A7A] text-white' 
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" style={{ 
                        color: selectedEventType === type ? 'white' : getEventColor(type) 
                      }} />
                      <span className="text-sm font-medium flex-1 text-left truncate">
                        {formatEventType(type)}
                      </span>
                      <span className={`text-sm font-bold ${
                        selectedEventType === type ? 'text-white' : 'text-[#1C3A7A]'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminEvents;
